import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription, BehaviorSubject, interval, EMPTY } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { PRESETS_PERIODO_SEM_ONTEM_E_HOJE } from '../../shared/utils/periodo.utils';
import { PedidoService } from '../../services/pedidos/pedido.service';
import { NotificacaoFcmService } from '../../services/notificacao/notificacao-fcm.service';
import { CarrinhoService } from '../../services/carrinho.service';
import { EmpresaConfigService } from '../../services/configuracoes/empresa.service';
import { EstadoDePedido, ESTADO_PEDIDO_CONFIG } from '../../enums/EstadoDePedido.enum';
import { NotificacaoPedidoDTO, TipoNotificacaoPedido } from '../../models/pedidos/notificacao-pedido.model';
import { PeriodoFiltro } from '../../models/dashboard/periodo-filtro.model';
import {
  fmtDateTime,
} from '../../shared/utils/periodo.utils';
import {
  PedidoConsultaDTO,
  PedidoDTO,
  PedidoFiltroDTO,
  CancelarItemPedidoDTO,
  ItemDePedidoConsultaDTO,
} from '../../models/pedidos/pedido.model';
import { PedidosPendentesService } from '../../services/pedidos/pedidos-pendentes.service';
import {
  ACAO_ENTREGAR,
  ACAO_ENTREGAR_E_FINALIZAR,
} from './componentes/pedido-estados-dialog/pedido-estados-dialog.component';
import { ImportarPedidoParaVendaService } from '../../services/pedidos/importar-pedido-para-venda.service';

// Mapeamento de prioridade dos estados para ordenacao
const ESTADO_ORDEM: Record<string, number> = {
  'ABERTO': 0,
  'EM_PRODUCAO': 1,
  'A_CAMINHO': 2,
  'AGUARDANDO_RETIRADA': 3,
  'ENTREGUE': 4,
  'FINALIZADO': 5,
  'CANCELADO': 6,
};

// Mapeamento de abas para estados
const ABAS_ESTADOS: Record<string, EstadoDePedido[]> = {
  'ABERTO': [EstadoDePedido.ABERTO],
  'EM_PROCESSO': [EstadoDePedido.EM_PRODUCAO, EstadoDePedido.A_CAMINHO,
                  EstadoDePedido.AGUARDANDO_RETIRADA, EstadoDePedido.ENTREGUE],
  'HISTORICO': [EstadoDePedido.FINALIZADO, EstadoDePedido.CANCELADO],
};

@Component({
  selector: 'app-pedidos',
  templateUrl: './pedidos.component.html',
  styleUrls: ['./pedidos.component.scss'],
  providers: [MessageService],
})
export class PedidosComponent implements OnInit, OnDestroy {

  // Opcoes do filtro de periodo (sem "Ontem e hoje")
  readonly opcoesPeriodo = PRESETS_PERIODO_SEM_ONTEM_E_HOJE;

  // Estado da listagem
  pedidos: PedidoConsultaDTO[] = [];
  loading = false;
  totalRegistros = 0;
  paginaAtual = 0;
  itensPorPagina = 10;
  private _ultimoItensPorPagina = 10;

  // Opcoes do seletor "Itens por pagina" (formato {label, value} igual as demais telas)
  pageSizeOptions = [
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
  ];

  /** Cards-fantasma exibidos enquanto carrega (ver `atualizarSkeletonSlots`). */
  skeletonSlots: number[] = [];

  // Quando o carregamento falha, a lista fica vazia — e antes disso a tela
  // exibia o MESMO estado vazio de "nao ha pedidos", mandando o usuario ajustar
  // filtros enquanto o problema era o servidor. Esta flag separa os dois casos.
  erroCarregamento = false;

  // Filtros
  abaAtiva = 'ABERTO';
  abasOptions: { label: string; value: string }[] = [];
  termoBusca = '';
  estadoFiltroSelecionado: EstadoDePedido | null = null;
  estadoFiltroOptions: { label: string; value: EstadoDePedido | null; icone?: string; cor?: string }[] = [];

  // Ordenacao
  ordenacaoSelecionada: string = 'ID';
  ordenacaoDirecao: 'asc' | 'desc' = 'asc';
  ordenacaoOptions: { label: string; value: string; icon: string }[] = [];

  // Periodo (via PeriodoFiltroComponent)
  periodoDataInicio = '';
  periodoDataFim = '';
  periodoTexto = '';

  // Bug #3: flag para pausar auto-refresh quando o dialog de filtro estiver aberto
  filtroPeriodoDialogAberto = false;

  // Dialog de detalhes
  dialogDetalheVisivel = false;
  pedidoSelecionado: PedidoDTO | null = null;
  loadingDetalhe = false;

  // Dialog de cancelamento
  dialogCancelarVisivel = false;
  pedidoParaCancelar: PedidoDTO | null = null;

  // Seletor de estados aberto pelo atalho do card (nao passa pelo detalhe)
  dialogEstadosCardVisivel = false;
  pedidoParaEstados: PedidoConsultaDTO | null = null;

  // Dialog de cancelar item
  dialogCancelarItemVisivel = false;
  itemParaCancelarId: number | null = null;
  quantidadeCancelar = 1;

  // Dialog de imprimir pedido (v2)
  dialogImprimirPedidoVisivel = false;
  pedidoParaImprimir: PedidoDTO | null = null;

  // Moeda padrao da empresa (carregada via EmpresaConfigService.getSessao()).
  // Defaults pre-configurados para Real (BRL) — sao sobrescritos quando a
  // resposta da API chega. Esses 3 campos sao propagados para os
  // subcomponentes (pedido-card e pedido-detalhe-dialog) via @Input
  // e usados nos templates atraves do pipe `moedaSimbolo`.
  moedaPadrao: 'BRL' | 'PYG' | 'USD' | 'ARS' = 'BRL';
  localeMoeda = 'pt-BR';
  digitsMoeda = '1.2-2';
  /** Simbolo curto da moeda (Gs., R$, $) usado apenas em mensagens WhatsApp/compartilhar. */
  private simboloMoedaTexto = 'R$';

  // Auto-refresh
  private readonly AUTO_REFRESH_INTERVAL = 30000; // 30 segundos
  autoRefreshAtivo = true;
  private autoRefreshAtivo$ = new BehaviorSubject<boolean>(true);
  private autoRefreshSub?: Subscription;

  // FCM (subscricao local para reload silencioso quando na tela de pedidos)
  private fcmSubscription?: Subscription;

  // Subscricao ao Router.events para abertura direta de pedido via notificacao
  private navSubscription?: Subscription;

  // Traducoes
  texts = {
    titulo: '',
    subtitulo: '',
    nenhum_pedido: '',
    nenhum_pedido_ainda: '',
    nenhum_resultado: '',
    ajuste_filtros: '',
    erro_carregar_titulo: '',
    erro_carregar_subtitulo: '',
    carregando: '',
    itens_do_pedido: '',
    alteracoes_pendentes: '',
    inclui_pendentes: '',
    titulo_descartar_alteracoes: '',
    confirmacao_descartar_alteracoes: '',
    resumo_pedido: '',
    confirmacao_cancelar: '',
    confirmacao_cancelar_item: '',
    auto_refresh_ativo: '',
    auto_refresh_inativo: '',
  };

  tooltips = {
    auto_refresh: '',
    avancar_producao: '',
    avancar_a_caminho: '',
    avancar_aguardando_retirada: '',
    ordenacao: '',
    confirme_alteracoes_pendentes: '',
    ordem_ascendente: '',
    ordem_descendente: '',
  };

  labels = {
    buscar: '',
    aberto: '',
    em_processo: '',
    historico: '',
    pedido: '',
    cliente: '',
    telefone: '',
    valor_total: '',
    observacao: '',
    estado: '',
    data: '',
    codigo: '',
    codigo_de_barras: '',
    item: '',
    quantidade: '',
    valor_unitario: '',
    saldo: '',
    qtd_itens: '',
    qtd_cancelada: '',
    qtd_faturada: '',
    itens_por_pagina: '',
    faturado: '',
    faturado_parcial: '',
    vendedor: '',
    todos_estados: '',
    ajustar_quantidade: '',
    adicionar_item_ao_pedido: '',
    pendente: '',
    variantes: '',
    modificadores: '',
    ordenar_por: '',
    ordenar_por_id: '',
    ordenar_por_estado: '',
    ordenar_por_timer: '',
    endereco_entrega: '',
    referencia_entrega: '',
    valor_frete: '',
    ver_mapa: '',
    pagamento: '',
    mesa: '',
    comanda: '',
    frete: '',
  };

  placeholders = {
    buscar_cliente_ou_numero: '',
  };

  botoes = {
    whatsapp: '',
    compartilhar: '',
    compartilhar_pedido: '',
    faturar: '',
    finalizar: '',
    entregar: '',
    entregar_e_finalizar: '',
    cancelar: '',
    cancelar_pedido: '',
    cancelar_item: '',
    confirmar: '',
    fechar: '',
    tentar_novamente: '',
    limpar_filtros: '',
    enviar_producao: '',
    enviar_a_caminho: '',
    aguardando_retirada: '',
    estados: '',
    adicionar_item: '',
    duplicar_item: '',
    confirmar_alteracoes: '',
    descartar_alteracoes: '',
    voltar_para_alteracoes: '',
  };

  estados = {
    ABERTO: '',
    EM_PRODUCAO: '',
    A_CAMINHO: '',
    AGUARDANDO_RETIRADA: '',
    ENTREGUE: '',
    FINALIZADO: '',
    CANCELADO: '',
  };

  tipo_entrega: Record<string, string> = {
    RETIRADA_NO_LOCAL: '',
    DELIVERY: '',
    CONSUMO_NO_LOCAL: '',
  };

  origem: Record<string, string> = {
    MINI_SHOP: '',
  };

  mensagens = {
    sucesso_estado: '',
    erro_estado: '',
    sucesso_cancelar: '',
    erro_cancelar: '',
    sucesso_cancelar_item: '',
    erro_cancelar_item: '',
    erro_adicionar_item: '',
    sucesso_confirmar_alteracoes: '',
    erro_confirmar_alteracoes: '',
    selecione_estado: '',
    sucesso_finalizar: '',
    sucesso_entregar: '',
    sucesso_entregar_finalizar: '',
    limite_saldo: '',
    sem_saldo_para_adicionar: '',
  };


  private langSubscription!: Subscription;
  private search$ = new Subject<string>();

  constructor(
    private pedidoService: PedidoService,
    private messageService: MessageService,
    public translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private carrinhoService: CarrinhoService,
    private fcmService: NotificacaoFcmService,
    private pedidosPendentesService: PedidosPendentesService,
    private empresaConfigService: EmpresaConfigService,
    private importarPedidoService: ImportarPedidoParaVendaService,
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.langSubscription = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });

    this.carregarMoedaDaEmpresa();
    this.setupSearchDebounce();
    this.inicializarPeriodoPadrao();
    this.atualizarSkeletonSlots();
    this.loadPedidos();
    this.iniciarAutoRefresh();

    // FCM - recarregar lista quando na tela de pedidos
    // A conexao FCM e o dialog global sao gerenciados pelo AppComponent
    this.fcmSubscription = this.fcmService.notificacoes$.subscribe(
      notificacao => this.onNotificacaoRecebida(notificacao)
    );

    // Abrir detalhe automaticamente se pedidoId vier via navigation state (foreground)
    // ou via query string ?pedidoId=N (background/service worker)
    this.verificarAberturaDiretaDePedido();

    // Escutar solicitacoes de abertura de pedido via servico compartilhado.
    // Usado quando o usuario clica "Ver" numa notificacao FCM enquanto ja esta
    // em /pedidos — same-URL navigation e ignorada pelo router, entao o
    // AppComponent comunica diretamente via PedidosPendentesService.abrirPedido$.
    this.navSubscription = this.pedidosPendentesService.abrirPedido$.subscribe(
      pedidoId => this.abrirDetalhePorId(pedidoId)
    );
  }

  ngOnDestroy(): void {
    this.langSubscription?.unsubscribe();
    this.search$.complete();
    this.autoRefreshSub?.unsubscribe();
    this.autoRefreshAtivo$.complete();
    this.fcmSubscription?.unsubscribe();
    this.navSubscription?.unsubscribe();
  }

  // ============================================================
  // Traducoes
  // ============================================================

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('pedidos.texts.titulo'),
      subtitulo: this.translate.instant('pedidos.texts.subtitulo'),
      nenhum_pedido: this.translate.instant('pedidos.texts.nenhum_pedido'),
      nenhum_pedido_ainda: this.translate.instant('pedidos.texts.nenhum_pedido_ainda'),
      nenhum_resultado: this.translate.instant('pedidos.texts.nenhum_resultado'),
      erro_carregar_titulo: this.translate.instant('pedidos.texts.erro_carregar_titulo'),
      erro_carregar_subtitulo: this.translate.instant('pedidos.texts.erro_carregar_subtitulo'),
      ajuste_filtros: this.translate.instant('pedidos.texts.ajuste_filtros'),
      carregando: this.translate.instant('pedidos.texts.carregando'),
      itens_do_pedido: this.translate.instant('pedidos.texts.itens_do_pedido'),
      alteracoes_pendentes: this.translate.instant('pedidos.texts.alteracoes_pendentes'),
      inclui_pendentes: this.translate.instant('pedidos.texts.inclui_pendentes'),
      titulo_descartar_alteracoes: this.translate.instant('pedidos.texts.titulo_descartar_alteracoes'),
      confirmacao_descartar_alteracoes: this.translate.instant('pedidos.texts.confirmacao_descartar_alteracoes'),
      resumo_pedido: this.translate.instant('pedidos.texts.resumo_pedido'),
      confirmacao_cancelar: this.translate.instant('pedidos.texts.confirmacao_cancelar'),
      confirmacao_cancelar_item: this.translate.instant('pedidos.texts.confirmacao_cancelar_item'),
      auto_refresh_ativo: this.translate.instant('pedidos.texts.auto_refresh_ativo'),
      auto_refresh_inativo: this.translate.instant('pedidos.texts.auto_refresh_inativo'),
    };

    this.tooltips = {
      auto_refresh: this.translate.instant('pedidos.tooltips.auto_refresh'),
      avancar_producao: this.translate.instant('pedidos.tooltips.avancar_producao'),
      avancar_a_caminho: this.translate.instant('pedidos.tooltips.avancar_a_caminho'),
      avancar_aguardando_retirada: this.translate.instant('pedidos.tooltips.avancar_aguardando_retirada'),
      ordenacao: this.translate.instant('pedidos.tooltips.ordenacao'),
      confirme_alteracoes_pendentes: this.translate.instant('pedidos.tooltips.confirme_alteracoes_pendentes'),
      ordem_ascendente: this.translate.instant('pedidos.tooltips.ordem_ascendente'),
      ordem_descendente: this.translate.instant('pedidos.tooltips.ordem_descendente'),
    };

    this.labels = {
      buscar: this.translate.instant('pedidos.labels.buscar'),
      aberto: this.translate.instant('pedidos.labels.aberto'),
      em_processo: this.translate.instant('pedidos.labels.em_processo'),
      historico: this.translate.instant('pedidos.labels.historico'),
      pedido: this.translate.instant('pedidos.labels.pedido'),
      cliente: this.translate.instant('pedidos.labels.cliente'),
      telefone: this.translate.instant('pedidos.labels.telefone'),
      valor_total: this.translate.instant('pedidos.labels.valor_total'),
      observacao: this.translate.instant('pedidos.labels.observacao'),
      estado: this.translate.instant('pedidos.labels.estado'),
      data: this.translate.instant('pedidos.labels.data'),
      codigo: this.translate.instant('pedidos.labels.codigo'),
      codigo_de_barras: this.translate.instant('pedidos.labels.codigo_de_barras'),
      item: this.translate.instant('pedidos.labels.item'),
      quantidade: this.translate.instant('pedidos.labels.quantidade'),
      valor_unitario: this.translate.instant('pedidos.labels.valor_unitario'),
      saldo: this.translate.instant('pedidos.labels.saldo'),
      qtd_itens: this.translate.instant('pedidos.labels.qtd_itens'),
      qtd_cancelada: this.translate.instant('pedidos.labels.qtd_cancelada'),
      qtd_faturada: this.translate.instant('pedidos.labels.qtd_faturada'),
      itens_por_pagina: this.translate.instant('pedidos.labels.itens_por_pagina'),
      faturado: this.translate.instant('pedidos.labels.faturado'),
      faturado_parcial: this.translate.instant('pedidos.labels.faturado_parcial'),
      vendedor: this.translate.instant('pedidos.labels.vendedor'),
      todos_estados: this.translate.instant('pedidos.labels.todos_estados'),
      ajustar_quantidade: this.translate.instant('pedidos.labels.ajustar_quantidade'),
      pendente: this.translate.instant('pedidos.labels.pendente'),
      adicionar_item_ao_pedido: this.translate.instant('pedidos.labels.adicionar_item_ao_pedido'),
      variantes: this.translate.instant('pedidos.labels.variantes'),
      modificadores: this.translate.instant('pedidos.labels.modificadores'),
      ordenar_por: this.translate.instant('pedidos.labels.ordenar_por'),
      ordenar_por_id: this.translate.instant('pedidos.labels.ordenar_por_id'),
      ordenar_por_estado: this.translate.instant('pedidos.labels.ordenar_por_estado'),
      ordenar_por_timer: this.translate.instant('pedidos.labels.ordenar_por_timer'),
      endereco_entrega: this.translate.instant('pedidos.labels.endereco_entrega'),
      referencia_entrega: this.translate.instant('pedidos.labels.referencia_entrega'),
      valor_frete: this.translate.instant('pedidos.labels.valor_frete'),
      ver_mapa: this.translate.instant('pedidos.labels.ver_mapa'),
      pagamento: this.translate.instant('pedidos.labels.pagamento'),
      mesa: this.translate.instant('pedidos.labels.mesa'),
      comanda: this.translate.instant('pedidos.labels.comanda'),
      frete: this.translate.instant('pedidos.labels.frete'),
    };

    this.placeholders = {
      buscar_cliente_ou_numero: this.translate.instant('pedidos.placeholders.buscar_cliente_ou_numero'),
    };

    this.botoes = {
      whatsapp: this.translate.instant('pedidos.botoes.whatsapp'),
      compartilhar: this.translate.instant('pedidos.botoes.compartilhar'),
      compartilhar_pedido: this.translate.instant('pedidos.botoes.compartilhar_pedido'),
      faturar: this.translate.instant('pedidos.botoes.faturar'),
      finalizar: this.translate.instant('pedidos.botoes.finalizar'),
      entregar: this.translate.instant('pedidos.botoes.entregar'),
      entregar_e_finalizar: this.translate.instant('pedidos.botoes.entregar_e_finalizar'),
      cancelar: this.translate.instant('pedidos.botoes.cancelar'),
      cancelar_pedido: this.translate.instant('pedidos.botoes.cancelar_pedido'),
      cancelar_item: this.translate.instant('pedidos.botoes.cancelar_item'),
      tentar_novamente: this.translate.instant('pedidos.botoes.tentar_novamente'),
      limpar_filtros: this.translate.instant('pedidos.botoes.limpar_filtros'),
      confirmar: this.translate.instant('pedidos.botoes.confirmar'),
      fechar: this.translate.instant('pedidos.botoes.fechar'),
      enviar_producao: this.translate.instant('pedidos.botoes.enviar_producao'),
      enviar_a_caminho: this.translate.instant('pedidos.botoes.enviar_a_caminho'),
      aguardando_retirada: this.translate.instant('pedidos.botoes.aguardando_retirada'),
      estados: this.translate.instant('pedidos.botoes.estados'),
      adicionar_item: this.translate.instant('pedidos.botoes.adicionar_item'),
      duplicar_item: this.translate.instant('pedidos.botoes.duplicar_item'),
      confirmar_alteracoes: this.translate.instant('pedidos.botoes.confirmar_alteracoes'),
      descartar_alteracoes: this.translate.instant('pedidos.botoes.descartar_alteracoes'),
      voltar_para_alteracoes: this.translate.instant('pedidos.botoes.voltar_para_alteracoes'),
    };

    this.estados = {
      ABERTO: this.translate.instant('pedidos.estados.ABERTO'),
      EM_PRODUCAO: this.translate.instant('pedidos.estados.EM_PRODUCAO'),
      A_CAMINHO: this.translate.instant('pedidos.estados.A_CAMINHO'),
      AGUARDANDO_RETIRADA: this.translate.instant('pedidos.estados.AGUARDANDO_RETIRADA'),
      ENTREGUE: this.translate.instant('pedidos.estados.ENTREGUE'),
      FINALIZADO: this.translate.instant('pedidos.estados.FINALIZADO'),
      CANCELADO: this.translate.instant('pedidos.estados.CANCELADO'),
    };

    this.tipo_entrega = {
      RETIRADA_NO_LOCAL: this.translate.instant('pedidos.tipo_entrega.RETIRADA_NO_LOCAL'),
      DELIVERY: this.translate.instant('pedidos.tipo_entrega.DELIVERY'),
      CONSUMO_NO_LOCAL: this.translate.instant('pedidos.tipo_entrega.CONSUMO_NO_LOCAL'),
    };

    this.origem = {
      MINI_SHOP: this.translate.instant('pedidos.origem.MINI_SHOP'),
    };

    this.mensagens = {
      sucesso_estado: this.translate.instant('pedidos.mensagens.sucesso_estado'),
      erro_estado: this.translate.instant('pedidos.mensagens.erro_estado'),
      sucesso_cancelar: this.translate.instant('pedidos.mensagens.sucesso_cancelar'),
      erro_cancelar: this.translate.instant('pedidos.mensagens.erro_cancelar'),
      sucesso_cancelar_item: this.translate.instant('pedidos.mensagens.sucesso_cancelar_item'),
      erro_cancelar_item: this.translate.instant('pedidos.mensagens.erro_cancelar_item'),
      erro_adicionar_item: this.translate.instant('pedidos.mensagens.erro_adicionar_item'),
      sucesso_confirmar_alteracoes: this.translate.instant('pedidos.mensagens.sucesso_confirmar_alteracoes'),
      erro_confirmar_alteracoes: this.translate.instant('pedidos.mensagens.erro_confirmar_alteracoes'),
      limite_saldo: this.translate.instant('pedidos.mensagens.limite_saldo'),
      sem_saldo_para_adicionar: this.translate.instant('pedidos.mensagens.sem_saldo_para_adicionar'),
      selecione_estado: this.translate.instant('pedidos.mensagens.selecione_estado'),
      sucesso_finalizar: this.translate.instant('pedidos.mensagens.sucesso_finalizar'),
      sucesso_entregar: this.translate.instant('pedidos.mensagens.sucesso_entregar'),
      sucesso_entregar_finalizar: this.translate.instant('pedidos.mensagens.sucesso_entregar_finalizar'),
    };

    this.abasOptions = [
      { label: this.labels.aberto, value: 'ABERTO' },
      { label: this.labels.em_processo, value: 'EM_PROCESSO' },
      { label: this.labels.historico, value: 'HISTORICO' },
    ];

    // Opcoes do dropdown de filtro por estado
    this.estadoFiltroOptions = [
      { label: this.labels.todos_estados, value: null, icone: 'pi pi-list', cor: '#6b7280' },
      ...Object.values(EstadoDePedido).map(e => ({
        label: this.estados[e] || e,
        value: e,
        icone: ESTADO_PEDIDO_CONFIG[e]?.icone || 'pi pi-circle',
        cor: ESTADO_PEDIDO_CONFIG[e]?.cor || '#999',
      })),
    ];

    // Opcoes do dropdown de ordenacao
    this.ordenacaoOptions = [
      { label: this.labels.ordenar_por_id, value: 'ID', icon: 'pi pi-hashtag' },
      { label: this.labels.ordenar_por_estado, value: 'ESTADO', icon: 'pi pi-flag' },
      { label: this.labels.ordenar_por_timer, value: 'TIMER', icon: 'pi pi-stopwatch' },
    ];
  }

  // ============================================================
  // Periodo (PeriodoFiltroComponent)
  // ============================================================

  private inicializarPeriodoPadrao(): void {
    // Periodo padrao "Mostrar todos" (showAll), alinhado ao app mobile e ao
    // valorPadrao='showAll' do app-periodo-filtro. Intervalo fixo
    // 01/01/2000 -> 31/12/3000.
    const start = new Date(2000, 0, 1, 0, 0);
    const end = new Date(3000, 11, 31, 23, 59);
    // Bug #1/#4: usar apenas a data sem hora para pre-definidos (backend cobre 00:00–23:59)
    this.periodoDataInicio = fmtDateTime(start).split('T')[0];
    this.periodoDataFim = fmtDateTime(end).split('T')[0];
  }

  onPeriodoChanged(periodo: PeriodoFiltro): void {
    // Bug #1/#4: preservar a hora quando o usuario definir horario customizado no modo Periodos.
    // O backend aceita DD/MM/YYYY (pre-definidos, cobre dia inteiro) e DD/MM/YYYYTHH:mm (periodos custom).
    this.periodoDataInicio = periodo.dataInicio;
    this.periodoDataFim = periodo.dataFim;
    this.periodoTexto = periodo.texto;
    this.paginaAtual = 0;
    this.loadPedidos();
  }

  // ============================================================
  // Busca com debounce
  // ============================================================

  private setupSearchDebounce(): void {
    this.search$.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(valor => {
      this.termoBusca = valor;
      this.paginaAtual = 0;
      this.loadPedidos();
    });
  }

  onSearchChange(valor: string): void {
    this.search$.next(valor);
  }

  // ============================================================
  // Filtros (abas)
  // ============================================================

  onAbaChange(): void {
    this.paginaAtual = 0;
    this.estadoFiltroSelecionado = null;
    this.loadPedidos();
  }

  onEstadoFiltroChange(): void {
    this.paginaAtual = 0;
    this.loadPedidos();
  }

  // ============================================================
  // Carregar dados
  // ============================================================

  loadPedidos(): void {
    this.atualizarSkeletonSlots();
    this.loading = true;
    this.erroCarregamento = false;
    const filtro = this.montarFiltro();

    this.pedidoService.get('/pedidos', filtro).subscribe({
      next: (response) => {
        this.pedidos = response.content || [];
        this.totalRegistros = response.totalElements || 0;
        this.aplicarOrdenacao();
        this.loading = false;
      },
      error: () => {
        this.pedidos = [];
        this.totalRegistros = 0;
        this.loading = false;
        // O toast some em segundos; a flag mantem o aviso na tela com um
        // caminho de saida ("tentar de novo"), em vez de deixar a tela
        // indistinguivel de "voce nao tem pedidos".
        this.erroCarregamento = true;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro_servidor'),
        });
      },
    });
  }

  /**
   * Ha algum filtro estreitando a lista? Serve para dizer ao usuario se a tela
   * esta vazia porque ele filtrou (e da para limpar) ou porque nao existe
   * pedido nenhum — sao situacoes diferentes e pedem mensagens diferentes.
   * A aba nao conta: ela sempre tem um valor e nao e algo que o usuario "limpa".
   */
  temFiltroAtivo(): boolean {
    return !!this.termoBusca?.trim() || !!this.estadoFiltroSelecionado;
  }

  /** Limpa busca e estado, devolvendo a lista completa da aba atual. */
  limparFiltros(): void {
    const tinhaBusca = !!this.termoBusca?.trim();
    this.estadoFiltroSelecionado = null;
    this.termoBusca = '';
    this.paginaAtual = 0;

    if (tinhaBusca) {
      // Emite no search$ para o distinctUntilChanged NAO ficar preso no termo
      // antigo — sem isso, digitar de novo o mesmo texto seria ignorado.
      // O proprio debounce recarrega a lista, por isso nao chamamos aqui.
      this.search$.next('');
    } else {
      this.loadPedidos();
    }
  }

  onPageChange(event: any): void {
    this.paginaAtual = event.page;
    this.itensPorPagina = event.rows;
    this.loadPedidos();
  }

  /**
   * Troca de "itens por pagina" pelo dropdown ao lado do paginador (padrao das
   * demais telas). Volta para a primeira pagina: manter o indice antigo com uma
   * pagina maior pode cair fora do total e devolver lista vazia.
   * A guarda evita recarregar quando o valor nao mudou de fato.
   */
  onRowsPerPageChange(): void {
    if (this.itensPorPagina === this._ultimoItensPorPagina) return;
    this._ultimoItensPorPagina = this.itensPorPagina;
    this.paginaAtual = 0;
    this.atualizarSkeletonSlots();
    this.loadPedidos();
  }

  /**
   * Quantidade de cards-fantasma: o tamanho da pagina, com teto de 8 (acima
   * disso o resto ja esta fora da viewport e so custa DOM).
   *
   * Usar a contagem atual da tela como estimativa foi testado e ficou PIOR —
   * entre abas a contagem nao se repete e o desvio total dobrou.
   */
  private atualizarSkeletonSlots(): void {
    this.skeletonSlots = Array(Math.min(this.itensPorPagina, 8)).fill(0);
  }

  // ============================================================
  // Auto-refresh
  // ============================================================

  /**
   * Inicia o timer de auto-refresh.
   * Usa interval + filter + switchMap para:
   * - Disparar a cada 30s
   * - Ignorar ticks quando toggle esta desativado
   * - Ignorar ticks quando dialog esta aberto
   * - Cancelar request anterior se ainda pendente (switchMap)
   */
  iniciarAutoRefresh(): void {
    this.autoRefreshSub = interval(this.AUTO_REFRESH_INTERVAL).pipe(
      filter(() => this.autoRefreshAtivo$.value),
      filter(() => !this.isDialogAberto()),
      switchMap(() => {
        const filtro = this.montarFiltro();
        return this.pedidoService.get('/pedidos', filtro).pipe(
          catchError(() => EMPTY)
        );
      })
    ).subscribe({
      next: (response) => {
        this.pedidos = response.content || [];
        this.totalRegistros = response.totalElements || 0;
        this.aplicarOrdenacao();
        // NAO altera this.loading (silencioso)
      },
      error: () => {
        // Silencioso: nao mostra erro no auto-refresh
        // O proximo tick tentara novamente
      },
    });
  }

  /** Alterna o estado do auto-refresh (ativo/inativo) */
  toggleAutoRefresh(): void {
    this.autoRefreshAtivo = !this.autoRefreshAtivo;
    this.autoRefreshAtivo$.next(this.autoRefreshAtivo);
  }

  /** Verifica se algum dialog esta aberto no componente */
  isDialogAberto(): boolean {
    return this.dialogDetalheVisivel || this.dialogCancelarVisivel || this.filtroPeriodoDialogAberto;
  }

  // Bug #3: handlers para pausar/retomar auto-refresh quando dialog de filtro abre/fecha
  onFiltroPeriodoDialogAberto(): void {
    this.filtroPeriodoDialogAberto = true;
  }

  onFiltroPeriodoDialogFechado(): void {
    this.filtroPeriodoDialogAberto = false;
  }

  /**
   * Monta o filtro de consulta de pedidos.
   * Extraido para reuso entre loadPedidos() e auto-refresh.
   */
  private montarFiltro(): PedidoFiltroDTO {
    let estadosStr: string;
    if (this.estadoFiltroSelecionado) {
      estadosStr = this.estadoFiltroSelecionado;
    } else {
      const estadosDaAba = ABAS_ESTADOS[this.abaAtiva] || [];
      estadosStr = estadosDaAba.map(e => e.toString()).join(',');
    }

    const filtro: PedidoFiltroDTO = {
      page: this.paginaAtual,
      size: this.itensPorPagina,
      estadoDePedido: estadosStr,
      dataInicial: this.periodoDataInicio,
      dataFinal: this.periodoDataFim,
    };

    if (this.termoBusca) {
      const termoLimpo = this.termoBusca.trim();
      if (/^\d+$/.test(termoLimpo)) {
        filtro.codigoDePedido = Number(termoLimpo);
      } else {
        filtro.nomePessoa = termoLimpo;
      }
    }

    return filtro;
  }

  // ============================================================
  // Detalhes do pedido
  // ============================================================

  /**
   * Verifica se deve abrir o dialog de detalhe automaticamente ao inicializar.
   * Le o pedidoId de duas fontes:
   * 1. history.state.abrirPedidoId: quando o usuario clica em "Ver" no dialog de notificacao (foreground)
   * 2. Query param ?pedidoId=N: quando clica na notificacao nativa do browser (background/SW)
   */
  private verificarAberturaDiretaDePedido(): void {
    // Fonte 1: navigation state via history.state (disponivel no ngOnInit apos navegacao)
    // O AppComponent passa { state: { abrirPedidoId: pedidoId } } ao navegar para /pedidos
    const pedidoIdState = history.state?.abrirPedidoId;
    // Limpar SEMPRE o abrirPedidoId do history.state, independente de ter valor ou nao.
    // Isso evita que states "velhos" preservados pelo browser (entre sessoes ou reloads)
    // reabram o modal automaticamente quando o componente e instanciado.
    if (history.state?.abrirPedidoId !== undefined) {
      const stateAtual = { ...history.state };
      delete stateAtual['abrirPedidoId'];
      history.replaceState(stateAtual, '');
    }
    if (pedidoIdState) {
      this.abrirDetalhePorId(Number(pedidoIdState));
      return;
    }

    // Fonte 2: query param (background - clique na notificacao nativa do browser via Service Worker)
    // Tambem usado pelo PDV ao redirecionar apos faturar um pedido (abrirPedidoId)
    this.route.queryParams.subscribe(params => {
      const pedidoIdQuery = params['pedidoId'];
      const abrirPedidoId = params['abrirPedidoId'];

      const idParaAbrir = pedidoIdQuery || abrirPedidoId;
      if (idParaAbrir) {
        // O PDV redireciona para ca apos faturar (abrirPedidoId). A aba
        // EM_PROCESSO cobre o faturamento parcial e o delivery; o faturamento
        // total nao-delivery hoje FINALIZA o pedido (aba Historico) — o detalhe
        // abre por id de qualquer forma, independente da aba ativa.
        if (abrirPedidoId && this.abaAtiva !== 'EM_PROCESSO') {
          this.abaAtiva = 'EM_PROCESSO';
          this.onAbaChange();
        }
        this.abrirDetalhePorId(Number(idParaAbrir));
        // Limpar o query param da URL sem recarregar a pagina
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {},
          replaceUrl: true,
        });
      }
    });
  }

  /** Busca o pedido pelo id e abre o dialog de detalhe */
  abrirDetalhePorId(pedidoId: number): void {
    if (!pedidoId) return;
    this.loadingDetalhe = true;
    this.dialogDetalheVisivel = true;

    this.pedidoService.getById(pedidoId).subscribe({
      next: (response) => {
        this.pedidoSelecionado = response;
        this.loadingDetalhe = false;
      },
      error: () => {
        this.loadingDetalhe = false;
        this.dialogDetalheVisivel = false;
      },
    });
  }

  /** Atalho do card: abre o seletor de estados sem carregar o detalhe. */
  onAbrirEstadosDoCard(pedido: PedidoConsultaDTO): void {
    this.pedidoParaEstados = pedido;
    this.dialogEstadosCardVisivel = true;
  }

  /**
   * Roteia a opcao escolhida no seletor aberto pelo card. Os handlers usam
   * apenas `pedido.id`, entao aceitam o DTO da listagem.
   */
  onAcaoEstadoDoCard(evento: { pedido: any; opcao: { acao: string } }): void {
    const { pedido, opcao } = evento;

    if (opcao.acao === ACAO_ENTREGAR) {
      this.onEntregarPedido(pedido);
      return;
    }
    if (opcao.acao === ACAO_ENTREGAR_E_FINALIZAR) {
      this.onEntregarEFinalizarPedido(pedido);
      return;
    }
    if (opcao.acao === 'cancelar') {
      this.pedidoParaCancelar = pedido;
      this.dialogCancelarVisivel = true;
      return;
    }
    this.onMudarEstado({ pedido, acao: opcao.acao });
  }

  onAbrirDetalhe(pedido: PedidoConsultaDTO): void {
    if (!pedido.id) return;
    this.loadingDetalhe = true;
    this.dialogDetalheVisivel = true;

    this.pedidoService.getById(pedido.id).subscribe({
      next: (response) => {
        this.pedidoSelecionado = response;
        this.loadingDetalhe = false;
      },
      error: () => {
        this.loadingDetalhe = false;
      },
    });
  }

  onFecharDetalhe(): void {
    this.dialogDetalheVisivel = false;
    this.pedidoSelecionado = null;
  }

  /**
   * Abre o dialog de impressao do pedido (v2) para o pedido selecionado.
   * O dialog fica fora do modal de detalhe — ambos convivem no template.
   * A geracao do PDF Jasper ocorre no backend; FE apenas consome via
   * `ImpressaoPedidoService.gerarPdfDePedido`.
   */
  onImprimirPedido(pedido: PedidoDTO): void {
    if (!pedido?.id) return;
    this.pedidoParaImprimir = pedido;
    this.dialogImprimirPedidoVisivel = true;
  }

  /**
   * Handler de mudanca de visibilidade do dialog de impressao.
   * Sincroniza o estado local e limpa o pedido quando fechado.
   */
  onImprimirPedidoVisibilidadeChange(visivel: boolean): void {
    this.dialogImprimirPedidoVisivel = visivel;
    if (!visivel) {
      this.pedidoParaImprimir = null;
    }
  }

  // ============================================================
  // Acoes de estado
  // ============================================================

  onMudarEstado(evento: { pedido: PedidoDTO; acao: string }): void {
    const { pedido, acao } = evento;
    if (!pedido.id) return;

    let observable$;

    switch (acao) {
      case 'enviarParaProducao':
        observable$ = this.pedidoService.enviarParaProducao(pedido.id);
        break;
      case 'enviarACaminho':
        observable$ = this.pedidoService.enviarACaminho(pedido.id);
        break;
      case 'enviarAguardandoRetirada':
        observable$ = this.pedidoService.enviarAguardandoRetirada(pedido.id);
        break;
      case 'cancelar':
        this.onCancelarPedido(pedido);
        return;
      default:
        return;
    }

    observable$.subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.mensagens.sucesso_estado,
        });
        this.dialogDetalheVisivel = false;
        this.pedidoSelecionado = null;
        this.loadPedidos();
        // Sincroniza o badge de pedidos pendentes no header apos mudanca de estado
        this.pedidosPendentesService.recarregar();
      },
    });
  }

  // ============================================================
  // Cancelar pedido
  // ============================================================

  onCancelarPedido(pedido: PedidoDTO): void {
    this.pedidoParaCancelar = pedido;
    this.dialogCancelarVisivel = true;
  }

  confirmarCancelamento(): void {
    if (!this.pedidoParaCancelar?.id) return;

    this.pedidoService.cancelarPedido(this.pedidoParaCancelar.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.mensagens.sucesso_cancelar,
        });
        this.dialogCancelarVisivel = false;
        this.dialogDetalheVisivel = false;
        this.pedidoParaCancelar = null;
        this.pedidoSelecionado = null;
        this.loadPedidos();
        // Sincroniza o badge de pedidos pendentes no header apos cancelamento
        this.pedidosPendentesService.recarregar();
      },
      error: () => {
        this.dialogCancelarVisivel = false;
        this.pedidoParaCancelar = null;
      },
    });
  }

  // ============================================================
  // Finalizar pedido
  // ============================================================

  onFinalizarPedido(pedido: PedidoDTO): void {
    if (!pedido?.id) return;
    this.pedidoService.finalizarPedido(pedido.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.mensagens.sucesso_finalizar || 'Pedido finalizado com sucesso',
        });
        this.dialogDetalheVisivel = false;
        this.pedidoSelecionado = null;
        this.loadPedidos();
      },
    });
  }

  /** Finalizar pedido acionado diretamente do card (estado ENTREGUE) */
  onFinalizarDoCard(pedidoConsulta: PedidoConsultaDTO): void {
    if (!pedidoConsulta?.id) return;
    this.pedidoService.finalizarPedido(pedidoConsulta.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.mensagens.sucesso_finalizar || 'Pedido finalizado com sucesso',
        });
        this.loadPedidos();
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: err?.error?.message || this.translate.instant('common.erro_servidor'),
        });
      },
    });
  }

  onEntregarPedido(pedido: PedidoDTO | PedidoConsultaDTO): void {
    if (!pedido?.id) return;
    this.pedidoService.entregar(pedido.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.mensagens.sucesso_entregar || 'Pedido entregue com sucesso',
        });
        this.dialogDetalheVisivel = false;
        this.pedidoSelecionado = null;
        this.loadPedidos();
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: err?.error?.message || this.translate.instant('common.erro_servidor'),
        });
      },
    });
  }

  onEntregarEFinalizarPedido(pedido: PedidoDTO): void {
    if (!pedido?.id) return;
    this.pedidoService.entregarEFinalizar(pedido.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.mensagens.sucesso_entregar_finalizar || 'Pedido entregue e finalizado com sucesso',
        });
        this.dialogDetalheVisivel = false;
        this.pedidoSelecionado = null;
        this.loadPedidos();
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: err?.error?.message || this.translate.instant('common.erro_servidor'),
        });
      },
    });
  }

  // ============================================================
  // Cancelar item
  // ============================================================

  onCancelarItem(dto: CancelarItemPedidoDTO): void {
    this.pedidoService.cancelarItem(dto).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.mensagens.sucesso_cancelar_item,
        });
        // Recarregar detalhe do pedido
        if (this.pedidoSelecionado?.id) {
          this.pedidoService.getById(this.pedidoSelecionado.id).subscribe({
            next: (response) => {
              this.pedidoSelecionado = response;
            },
          });
        }
        this.loadPedidos();
      },
    });
  }

  // ============================================================
  // Adicionar item / alterar quantidade (grade de produtos)
  // ============================================================

  /** Persiste a alteracao de quantidade de um item do pedido (via +/-). */
  onAlterarQuantidadeItem(evento: { itemDePedidoId: number; quantidade: number }): void {
    this.pedidoService.alterarQuantidadeItem(evento.itemDePedidoId, evento.quantidade).subscribe({
      next: (pedido) => {
        this.aplicarPedidoAtualizado(pedido);
        this.loadPedidos();
        this.pedidosPendentesService.recarregar();
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: err?.error?.message || this.translate.instant('common.erro_servidor'),
        });
        // Reverter a UI otimista recarregando o detalhe a partir do backend.
        this.recarregarDetalheSelecionado();
      },
    });
  }

  /** Recebe o pedido atualizado apos adicionar item pela grade de produtos. */
  onPedidoAtualizado(pedido: PedidoConsultaDTO): void {
    this.aplicarPedidoAtualizado(pedido);
    this.loadPedidos();
    this.pedidosPendentesService.recarregar();
  }

  /** Mescla o pedido atualizado no pedido selecionado, preservando campos extras. */
  private aplicarPedidoAtualizado(pedido: PedidoConsultaDTO): void {
    if (!pedido) return;
    this.pedidoSelecionado = { ...(this.pedidoSelecionado || {}), ...pedido } as PedidoDTO;
  }

  /** Recarrega o detalhe do pedido selecionado a partir do backend. */
  private recarregarDetalheSelecionado(): void {
    if (!this.pedidoSelecionado?.id) return;
    this.pedidoService.getById(this.pedidoSelecionado.id).subscribe({
      next: (response) => {
        this.pedidoSelecionado = response;
      },
    });
  }

  // ============================================================
  // Faturar (navegar ao PDV)
  // ============================================================

  /** Abre a nota fiscal no modulo de notas */
  onAbrirNota(notaId: number): void {
    this.dialogDetalheVisivel = false;
    this.router.navigate(['/notas-list/fatura', notaId]);
  }

  /** Faturar direto do card - busca pedido completo e navega ao PDV */
  onFaturarDoCard(pedidoConsulta: PedidoConsultaDTO): void {
    if (!pedidoConsulta?.id) return;
    this.pedidoService.getById(pedidoConsulta.id).subscribe({
      next: (pedido) => {
        const itensParaFaturar = this.importarPedidoService.itensComSaldo(pedido.itens);
        if (itensParaFaturar.length === 0) return;
        this.onFaturar({ pedido, itens: itensParaFaturar });
      },
    });
  }

  onFaturar(evento: { pedido: PedidoDTO; itens: ItemDePedidoConsultaDTO[] }): void {
    const { pedido, itens } = evento;

    // A conversao pedido -> carrinho vive no service porque o popup "Importar pedido" da tela de
    // venda faz exatamente a mesma coisa pelo caminho inverso.
    const importou = this.importarPedidoService.aplicarNoCarrinho(pedido, itens);
    if (!importou) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: this.translate.instant('pedidos.mensagens.sem_itens_faturar'),
      });
      return;
    }

    this.dialogDetalheVisivel = false;
    this.pedidoSelecionado = null;
    this.router.navigate(['/vendas']);
  }

  private getEmpresaId(): string {
    return sessionStorage.getItem('id') || 'semEmpresa';
  }

  // ============================================================
  // Notificacoes FCM
  // ============================================================

  /**
   * Handler de notificacao recebida via FCM.
   * O dialog e som sao tratados globalmente pelo AppComponent.
   * Aqui apenas recarregamos a lista de pedidos silenciosamente.
   */
  private onNotificacaoRecebida(notificacao: NotificacaoPedidoDTO): void {
    if (
      notificacao.tipo === TipoNotificacaoPedido.NOVO_PEDIDO ||
      notificacao.tipo === TipoNotificacaoPedido.MUDANCA_ESTADO
    ) {
      this.loadPedidosSilencioso();
    }
  }

  /** Recarrega lista sem alterar loading (silencioso) */
  private loadPedidosSilencioso(): void {
    const filtro = this.montarFiltro();
    this.pedidoService.get('/pedidos', filtro).subscribe({
      next: (response) => {
        this.pedidos = response.content || [];
        this.totalRegistros = response.totalElements || 0;
        this.aplicarOrdenacao();
      },
      error: (err) => {
        // Silencioso: erro de rede nao interrompe o usuario
        console.warn('[Pedidos] Erro no reload silencioso via FCM:', err);
      },
    });
  }

  // ============================================================
  // WhatsApp
  // ============================================================

  onAbrirWhatsApp(pedido: PedidoConsultaDTO | PedidoDTO): void {
    if (!pedido.telefone) return;
    const telefone = pedido.telefone.replace(/\D/g, '');
    const mensagem = this.montarMensagemWhatsApp(pedido);
    const url = `https://wa.me/${telefone}?text=${encodeURIComponent(mensagem)}`;
    window.open(url, '_blank');
  }

  private montarMensagemWhatsApp(pedido: PedidoConsultaDTO | PedidoDTO): string {
    const linhas: string[] = [];

    // Cabecalho
    linhas.push(`🛒 ${this.labels.pedido} #${pedido.codigoDePedido}`);
    linhas.push('');

    // Cliente
    linhas.push(`👤 ${this.labels.cliente}: ${pedido.nomeCliente || '-'}`);
    if (pedido.telefone) {
      const tel = pedido.telefone.replace(/\D/g, '');
      linhas.push(`📱 ${this.labels.telefone}: https://api.whatsapp.com/send?phone=${tel}`);
    }
    linhas.push('');

    // Endereco de entrega (apenas para PedidoDTO que tem esses campos)
    const pedidoDTO = pedido as PedidoDTO;
    if (pedidoDTO.enderecoDeEntrega) {
      linhas.push(`📍 ${this.labels.endereco_entrega}:`);
      linhas.push(pedidoDTO.enderecoDeEntrega);
      if (pedidoDTO.referenciaDeEntrega) {
        linhas.push(`🏠 ${this.labels.referencia_entrega}: ${pedidoDTO.referenciaDeEntrega}`);
      }
      if (pedidoDTO.latitudeEntrega && pedidoDTO.longitudeEntrega) {
        linhas.push(`🗺️ ${this.labels.ver_mapa}: https://www.google.com/maps?q=${pedidoDTO.latitudeEntrega},${pedidoDTO.longitudeEntrega}`);
      }
      linhas.push('');
    }

    // Itens
    if (pedido.itens && pedido.itens.length > 0) {
      linhas.push(`📦 ${this.labels.item}:`);
      pedido.itens.forEach(item => {
        const nome = item.item?.nome || item.produtoCardapioNome || '-';
        linhas.push(`  • ${item.quantidade}x ${nome} — ${this.simboloMoedaTexto} ${this.formatarValor(item.valorTotal)}`);
      });
      linhas.push('');
    }

    // Total e forma de pagamento
    linhas.push(`💰 ${this.labels.valor_total}: ${this.simboloMoedaTexto} ${this.formatarValor(pedido.valorTotal)}`);

    // Forma de pagamento: prefere o campo estruturado. Pedidos anteriores a esse campo
    // vem com null, entao mantemos o fallback pelo texto da observacao
    // (padrao Mini Shop: "... | Pagamento: Efectivo") para nao perder o historico.
    const formaDePagamento = this.rotuloFormaDePagamento((pedido as any).formaDePagamento);
    if (formaDePagamento) {
      linhas.push(`💳 ${this.labels.pagamento}: ${formaDePagamento}`);
    } else if (pedido.observacao) {
      const matchPagamento = pedido.observacao.match(/Pagamento:\s*([^|]+)/i);
      if (matchPagamento) {
        linhas.push(`💳 ${this.labels.pagamento}: ${matchPagamento[1].trim()}`);
      }
    }

    return linhas.join('\n');
  }

  /**
   * Traduz o campo estruturado de forma de pagamento do pedido.
   *
   * O backend serializa enums como objeto (`{ enum: 'TARJETA' }`) ou como string,
   * dependendo do endpoint — os dois formatos sao tratados aqui.
   */
  private rotuloFormaDePagamento(forma: any): string | null {
    if (!forma) return null;
    const nome = typeof forma === 'object' ? (forma.enum ?? forma.value ?? null) : forma;
    if (!nome) return null;

    const chaves: Record<string, string> = {
      DINHEIRO: 'criar_pedido.forma_pagamento_efectivo',
      TARJETA: 'criar_pedido.forma_pagamento_tarjeta',
      QR_CODE: 'criar_pedido.forma_pagamento_qrcode',
      TRANSFERENCIA: 'criar_pedido.forma_pagamento_transferencia',
    };
    const chave = chaves[String(nome).toUpperCase()];
    return chave ? this.translate.instant(chave) : String(nome);
  }

  // ============================================================
  // Compartilhar
  // ============================================================

  onCompartilhar(pedido: PedidoConsultaDTO | PedidoDTO): void {
    const texto = this.montarMensagemCompartilhar(pedido);
    const url = `https://wa.me/?text=${encodeURIComponent(texto)}`;
    window.open(url, '_blank');
  }

  private montarMensagemCompartilhar(pedido: PedidoConsultaDTO | PedidoDTO): string {
    const linhas: string[] = [];

    // Cabecalho
    linhas.push(`🔥 NUEVO PEDIDO #${pedido.codigoDePedido}`);
    linhas.push('');

    // Itinerario (se tiver coordenadas)
    // PedidoDTO expoe latitudeEntrega/longitudeEntrega, PedidoConsultaDTO expoe latitude/longitude
    const pedidoAny = pedido as PedidoDTO & PedidoConsultaDTO;
    const lat = pedidoAny.latitudeEntrega ?? pedidoAny.latitude;
    const lng = pedidoAny.longitudeEntrega ?? pedidoAny.longitude;
    if (lat && lng) {
      linhas.push('🗺️ ITINERARIO');
      linhas.push(`Itinerario: https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`);
      linhas.push('');
    }

    // Cliente
    linhas.push('👤 CLIENTE');
    linhas.push(`Nombre: ${pedido.nomeCliente || '-'}`);
    if (pedido.telefone) {
      const tel = pedido.telefone.replace(/\D/g, '');
      linhas.push(`Teléfono: https://api.whatsapp.com/send?phone=${tel}`);
    }
    linhas.push('');

    // Pago
    linhas.push('💲 PAGO');
    linhas.push(`Total: ${this.simboloMoedaTexto} ${this.formatarValor(pedido.valorTotal)}`);
    linhas.push('');

    // Pedido (itens)
    if (pedido.itens && pedido.itens.length > 0) {
      linhas.push('📦 PEDIDO');
      pedido.itens.forEach(item => {
        const nome = item.item?.nome || item.produtoCardapioNome || '-';
        linhas.push(`- ${item.quantidade} x ${nome}`);
      });
    }

    return linhas.join('\n');
  }

  // ============================================================
  // Ordenacao
  // ============================================================

  onOrdenacaoChange(): void {
    this.aplicarOrdenacao();
  }

  toggleOrdenacao(): void {
    this.ordenacaoDirecao = this.ordenacaoDirecao === 'desc' ? 'asc' : 'desc';
    this.aplicarOrdenacao();
  }

  aplicarOrdenacao(): void {
    const dir = this.ordenacaoDirecao === 'asc' ? 1 : -1;

    switch (this.ordenacaoSelecionada) {
      case 'ID':
        this.pedidos.sort((a, b) => dir * ((a.codigoDePedido || 0) - (b.codigoDePedido || 0)));
        break;

      case 'ESTADO':
        this.pedidos.sort((a, b) => {
          const estadoA = ESTADO_ORDEM[this.resolveEstado(a.estadoDePedido)] ?? 99;
          const estadoB = ESTADO_ORDEM[this.resolveEstado(b.estadoDePedido)] ?? 99;
          if (estadoA !== estadoB) return dir * (estadoA - estadoB);
          return dir * ((a.codigoDePedido || 0) - (b.codigoDePedido || 0));
        });
        break;

      case 'TIMER':
        this.pedidos.sort((a, b) => {
          const dateA = a.emissao ? new Date(a.emissao).getTime() : 0;
          const dateB = b.emissao ? new Date(b.emissao).getTime() : 0;
          return dir * (dateA - dateB);
        });
        break;
    }
  }

  // ============================================================
  // Helpers
  // ============================================================

  /**
   * Resolve o estado do pedido para string.
   * O backend serializa enums LocalizableEnum como objeto { enum: "ABERTO", nome: "Aberto", ... }
   */
  resolveEstado(estado: any): EstadoDePedido {
    if (typeof estado === 'string') return estado as EstadoDePedido;
    if (estado?.enum) return estado.enum as EstadoDePedido;
    if (estado?.name) return estado.name as EstadoDePedido;
    return EstadoDePedido.ABERTO;
  }

  getCorEstado(estado: any): string {
    return ESTADO_PEDIDO_CONFIG[this.resolveEstado(estado)]?.cor || '#999';
  }

  getIconeEstado(estado: any): string {
    return ESTADO_PEDIDO_CONFIG[this.resolveEstado(estado)]?.icone || 'pi pi-circle';
  }

  getEstadoLabel(estado: any): string {
    const resolved = this.resolveEstado(estado);
    return this.estados[resolved] || (typeof estado === 'object' && estado?.nome ? estado.nome : resolved);
  }

  /**
   * Formata um valor monetario para exibicao em mensagens de texto puro
   * (WhatsApp / Compartilhar). Os templates HTML usam o pipe `moedaSimbolo`
   * diretamente — este metodo serve apenas para strings montadas no TS.
   *
   * Respeita o locale e as casas decimais da moeda padrao da empresa logada.
   * O simbolo curto (Gs., R$, $) e prefixado no texto via `simboloMoedaTexto`.
   */
  formatarValor(valor: number): string {
    if (valor == null || isNaN(valor)) return '-';
    const casas = this.digitsMoeda === '1.0-0' ? 0 : 2;
    return new Intl.NumberFormat(this.localeMoeda, {
      minimumFractionDigits: casas,
      maximumFractionDigits: casas,
    }).format(valor);
  }

  /**
   * Carrega a moeda padrao da empresa logada (ISO, locale e casas decimais)
   * para que todos os valores monetarios da tela de Pedidos sejam exibidos
   * conforme a configuracao da empresa (R$ para BRL, $ para USD/ARS, Gs. para PYG).
   *
   * Replica o padrao usado em vendas.component.ts.
   */
  private carregarMoedaDaEmpresa(): void {
    this.empresaConfigService.getSessao().subscribe({
      next: (res) => {
        const moeda = res?.moeda;
        const codigoOperacao =
          moeda?.codigoOperacao ??
          (moeda?.enum === 'REAL'
            ? 'BRL'
            : moeda?.enum === 'DOLAR'
              ? 'USD'
              : moeda?.enum === 'GUARANI'
                ? 'PYG'
                : 'BRL');

        this.moedaPadrao = codigoOperacao as 'BRL' | 'PYG' | 'USD' | 'ARS';

        const rawLocal = moeda?.pais?.local || 'pt_BR';
        this.localeMoeda = String(rawLocal).replace('_', '-');

        const casas = Number(moeda?.casasDecimais ?? (codigoOperacao === 'PYG' ? 0 : 2));
        this.digitsMoeda = casas === 0 ? '1.0-0' : '1.2-2';

        this.simboloMoedaTexto = this.resolverSimboloTextoDaMoeda(this.moedaPadrao);
      },
      error: () => {
        // Fallback: mantem defaults Real (BRL).
      },
    });
  }

  /**
   * Retorna o simbolo curto da moeda para uso em mensagens de texto puro
   * (WhatsApp / Compartilhar), onde nao podemos usar o pipe `moedaSimbolo`.
   */
  private resolverSimboloTextoDaMoeda(iso: 'BRL' | 'PYG' | 'USD' | 'ARS'): string {
    switch (iso) {
      case 'BRL': return 'R$';
      case 'PYG': return 'Gs.';
      case 'USD': return '$';
      case 'ARS': return '$';
      default: return '';
    }
  }

  formatarDataExibicao(dataStr?: string): string {
    if (!dataStr) return '-';
    try {
      const data = new Date(dataStr);
      return data.toLocaleDateString(this.translate.currentLang || 'pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dataStr;
    }
  }

  formatarHora(dataStr?: string): string {
    if (!dataStr) return '';
    try {
      const data = new Date(dataStr);
      return data.toLocaleTimeString(this.translate.currentLang || 'pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  }
}
